import { useEffect, useState } from 'react';
import {
  api, type BrandRow, type ImportResult, type KosherType, type MasterDetail, type OfferDetail, type SupplierOption, type UomRow,
} from '../api';
import { money, ts, unit, useDebounced, useLoad } from '../lib';
import {
  CategoriesCard, CategorySelect, ChangeLog, DietPills, ImportOutcome, MagentoNote, Outcome, pack, Pager, QUICK_QUOTE_CATEGORY, SelectAll,
  StatusPill, SupplierPicker, TriSelect, useSelection,
} from './catalog-ui';

// The catalog consoles (ADOBE_ADMIN_PARITY 2026-10-16, Tier 1 #2) — what production's Catalog menu
// does: Master Products and Supplier Products with their cleanup views, bulk status and attribute
// updates, Add Quick Quote and Add Supplier Item, the exports, Units of Measure, Product Brands, and the
// Import Supplier Products (availability) file. Commerce writes and audits every change.

const SETS: Record<string, string> = { '24': 'Food', '4': 'Kitchen/Janitorial', '23': 'Medical', '26': 'Program' };
const KOSHER_TYPES: KosherType[] = ['Pareve', 'Dairy', 'Cholov Yisroel'];
const qsOf = (p: Record<string, string>) => new URLSearchParams(Object.entries(p).filter(([, v]) => v !== '')).toString();

// ── Masters ───────────────────────────────────────────────────────────────────────────────────────
const MASTER_VIEWS = [
  ['', 'All masters'], ['not_associated', 'Not associated'], ['without_bids', 'Without bids'], ['without_orders', 'Without orders'],
] as const;

export function MastersPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  const [f, setF] = useState({ view: '', status: '', set: '', kosher: '', gluten_free: '', vegan: '' });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [nonce, setNonce] = useState(0);
  const sel = useSelection();
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const params = { q: dq, ...f };
  const { data, err: loadErr } = useLoad(() => api.catalogMasters({ ...params, page: String(page) }), [JSON.stringify(params), page, nonce]);
  useEffect(() => { setPage(1); }, [JSON.stringify(params)]);

  async function bulk(set: Record<string, unknown>, what: string) {
    setMsg(''); setErr('');
    try {
      const r = await api.catalogBulkMasters([...sel.sel], set);
      setMsg(`${what}: ${r.changed.toLocaleString()} of ${r.selected.toLocaleString()} masters changed.`);
      sel.clear(); setNonce((n) => n + 1);
    } catch (e) { setErr((e as Error).message); }
  }

  if (open != null) return <MasterDetailPane id={open} canEdit={canEdit} onBack={() => { setOpen(null); setNonce((n) => n + 1); }} />;
  if (adding) return <QuickQuotePane onDone={(id) => { setAdding(false); if (id) setOpen(id); }} />;
  const ids = data?.rows.map((r) => r.id) ?? [];

  return (
    <div className="stack">
      <h1>Master products</h1>
      <p className="sub">The products camps order — each groups the supplier products that can fill it</p>
      <div className="toolbar">
        {MASTER_VIEWS.map(([v, l]) => <button key={v} className={`chip${f.view === v ? ' on' : ''}`} onClick={() => setF({ ...f, view: v })}>{l}</button>)}
      </div>
      <div className="toolbar">
        <input className="search" placeholder="Search SKU or name (* wildcard)…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="sel" aria-label="Status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="">Any status</option><option value="1">Enabled</option><option value="2">Disabled</option>
        </select>
        <select className="sel" aria-label="Product type" value={f.set} onChange={(e) => setF({ ...f, set: e.target.value })}>
          <option value="">Any type</option>{Object.entries(SETS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {(['kosher', 'gluten_free', 'vegan'] as const).map((k) => (
          <button key={k} className={`chip${f[k] === '1' ? ' on' : ''}`} onClick={() => setF({ ...f, [k]: f[k] === '1' ? '' : '1' })}>
            {k === 'gluten_free' ? 'Gluten-free' : k === 'kosher' ? 'Kosher' : 'Vegan'}
          </button>
        ))}
        <span className="toolbar-end">
          <a className="btn-sm ghost" href={`/api/commerce/catalog/masters-export.csv?${qsOf(params)}`} download>Export CSV</a>
          {canEdit && <button className="btn-sm" onClick={() => setAdding(true)}>Add Quick Quote</button>}
        </span>
      </div>
      <Outcome msg={msg} err={err || loadErr || ''} />
      {canEdit && sel.sel.size > 0 && (
        <BulkBar count={sel.sel.size} onClear={sel.clear} actions={[
          ['Enable', () => bulk({ status: 1 }, 'Enabled')],
          ['Disable', () => bulk({ status: 2 }, 'Disabled')],
        ]} flags={['kosher', 'gluten_free', 'vegan', 'only_as_specified']} onFlag={(k, v) => bulk({ [k]: v }, `${k.replace(/_/g, ' ')} set to ${v ? 'yes' : 'no'}`)} />
      )}
      <section className="card">
        <div className="table-wrap">
          <table>
            <thead><tr>
              {canEdit && <th className="chk-col"><SelectAll ids={ids} selection={sel} /></th>}
              <th>Master SKU</th><th>Name</th><th>Type</th><th>Brand</th><th>Dietary</th><th className="num">Supplier products</th><th>Status</th>
            </tr></thead>
            <tbody>
              {data?.rows.map((m) => (
                <tr key={m.id} className="row-click" onClick={() => setOpen(m.id)}>
                  {canEdit && <td className="chk-col" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Select ${m.sku}`} checked={sel.has(m.id)} onChange={() => sel.toggle(m.id)} /></td>}
                  <td className="mono">{m.sku}</td>
                  <td style={{ fontWeight: 600 }}>{m.name ?? '—'}</td>
                  <td>{m.attribute_set_type ?? '—'}</td>
                  <td>{m.brand ?? '—'}</td>
                  <td><DietPills d={m} /></td>
                  <td className="num">{m.associations ?? 0}</td>
                  <td><StatusPill status={m.status} /></td>
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={8} className="loading">No masters match.</td></tr>}
            </tbody>
          </table>
        </div>
        {!data && !loadErr && <p className="loading">Loading…</p>}
        {data && <Pager page={data.page} per={data.per} total={data.total} onPage={setPage} />}
      </section>
    </div>
  );
}

/** The bulk bar over a selection: named actions, and "set flag yes / no". */
function BulkBar({ count, onClear, actions, flags, onFlag }: {
  count: number; onClear: () => void; actions: Array<[string, () => void]>; flags?: string[]; onFlag?: (k: string, v: boolean) => void;
}) {
  const [flag, setFlag] = useState('');
  return (
    <div className="bulk-bar">
      <strong>{count.toLocaleString()} selected</strong>
      {actions.map(([l, fn]) => <button key={l} className="btn-sm" onClick={fn}>{l}</button>)}
      {flags && onFlag && (
        <span className="inline-field">
          Set
          <select className="sel" aria-label="Attribute to set" value={flag} onChange={(e) => setFlag(e.target.value)}>
            <option value="">attribute…</option>
            {flags.map((k) => <option key={k} value={k}>{k === 'only_as_specified' ? "spec'd item" : k.replace(/_/g, ' ')}</option>)}
          </select>
          <button className="btn-sm ghost" disabled={!flag} onClick={() => onFlag(flag, true)}>Yes</button>
          <button className="btn-sm ghost" disabled={!flag} onClick={() => onFlag(flag, false)}>No</button>
        </span>
      )}
      <button className="link" onClick={onClear}>Clear selection</button>
    </div>
  );
}

/** A brand typeahead over the brand list; shows the current brand's name. */
function BrandPicker({ id, name, onChange }: { id: string | null; name: string | null; onChange: (id: string | null, name: string | null) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [opts, setOpts] = useState<BrandRow[]>([]);
  useEffect(() => {
    if (!dq) { setOpts([]); return; }
    let live = true;
    api.catalogBrands(dq).then((r) => live && setOpts(r.rows.slice(0, 12))).catch(() => {});
    return () => { live = false; };
  }, [dq]);
  return (
    <label className="field"><span>Brand</span>
      {id ? (
        <span className="picked-row" style={{ margin: 0 }}>
          <button type="button" className="pill pick" title="Clear the brand" onClick={() => onChange(null, null)}>{name ?? `Brand ${id}`} ✕</button>
        </span>
      ) : (
        <>
          <input className="search" placeholder="Search brands…" value={q} onChange={(e) => setQ(e.target.value)} />
          {opts.length > 0 && (
            <div className="typeahead">
              {opts.map((b) => <button type="button" key={b.id} className="ta-opt" onClick={() => { onChange(String(b.id), b.name); setQ(''); }}>{b.name}</button>)}
            </div>
          )}
        </>
      )}
    </label>
  );
}

function MasterDetailPane({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const [d, setD] = useState<MasterDetail | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [brandName, setBrandName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const load = () => api.catalogMaster(id).then((r) => {
    setD(r);
    const m = r.master;
    setForm({ sku: m.sku, name: m.name ?? '', status: m.status, attribute_set_id: m.attribute_set_id, brand_id: m.brand_id,
      only_as_specified: m.only_as_specified, kosher: m.kosher, kosher_type: m.kosher_type, gluten_free: m.gluten_free, vegan: m.vegan });
    setBrandName(m.brand);
  }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, [id]);

  if (!d) return <div className="stack"><div><button className="btn-sm ghost" onClick={onBack}>← Back to masters</button></div>{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  const m = d.master;
  const original: Record<string, unknown> = { sku: m.sku, name: m.name ?? '', status: m.status, attribute_set_id: m.attribute_set_id, brand_id: m.brand_id,
    only_as_specified: m.only_as_specified, kosher: m.kosher, kosher_type: m.kosher_type, gluten_free: m.gluten_free, vegan: m.vegan };
  const patch = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== original[k]));
  const set = (k: string, v: unknown) => setForm({ ...form, [k]: v });

  async function save() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.catalogSaveMaster(id, patch);
      setMsg(r.changed.length ? `Saved: ${r.changed.map((c) => c.replace(/_/g, ' ')).join(', ')}.` : 'Nothing changed.');
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to masters</button></div>
      <h1>{m.name ?? m.sku}</h1>
      <p className="sub">Master {m.id} · <span className="mono">{m.sku}</span>{m.attribute_set_type ? ` · ${m.attribute_set_type}` : ''}{m.created_at ? ` · created ${ts(m.created_at)}` : ''}</p>
      <Outcome msg={msg} err={err} />
      <section className="card">
        <h2>Details</h2>
        <fieldset className="plain" disabled={!canEdit}>
          <div className="tf-grid">
            <label>Name<input value={String(form.name ?? '')} onChange={(e) => set('name', e.target.value)} /></label>
            <label>Master SKU<input value={String(form.sku ?? '')} onChange={(e) => set('sku', e.target.value)} /></label>
            <label>Status
              <select value={String(form.status)} onChange={(e) => set('status', Number(e.target.value))}><option value="1">Enabled</option><option value="2">Disabled</option></select>
            </label>
            <label>Product type
              <select value={String(form.attribute_set_id ?? '')} onChange={(e) => set('attribute_set_id', Number(e.target.value))}>
                {!SETS[String(form.attribute_set_id)] && <option value="">—</option>}
                {Object.entries(SETS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <BrandPicker id={(form.brand_id as string | null) ?? null} name={brandName} onChange={(bid, bn) => { setBrandName(bn); set('brand_id', bid); }} />
            <label>Spec'd item<TriSelect label="Spec'd item" value={form.only_as_specified as boolean | null} onChange={(v) => set('only_as_specified', v)} /></label>
            <label>Kosher<TriSelect label="Kosher" value={form.kosher as boolean | null} onChange={(v) => set('kosher', v)} /></label>
            <label>Kosher type
              <select value={String(form.kosher_type ?? '')} onChange={(e) => set('kosher_type', e.target.value || null)}>
                <option value="">—</option>{KOSHER_TYPES.map((k) => <option key={k}>{k}</option>)}
              </select>
            </label>
            <label>Gluten-free<TriSelect label="Gluten-free" value={form.gluten_free as boolean | null} onChange={(v) => set('gluten_free', v)} /></label>
            <label>Vegan<TriSelect label="Vegan" value={form.vegan as boolean | null} onChange={(v) => set('vegan', v)} /></label>
          </div>
        </fieldset>
        {canEdit && (
          <div className="edi-actions">
            <span className="muted">Disabling a master hides it from every camp; its supplier products and bids are kept, so enabling it brings it back whole.</span>
            <button className="btn-sm" disabled={busy || Object.keys(patch).length === 0} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        )}
        {canEdit && <MagentoNote />}
      </section>

      <section className="card">
        <h2>Supplier products ({d.associations.length})</h2>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Supplier</th><th>Vendor item #</th><th>Pack</th><th className="num">Price</th><th className="num">Unit price</th><th>Bids</th><th>Status</th></tr></thead>
            <tbody>
              {d.associations.map((a) => (
                <tr key={a.id}>
                  <td>{a.vendor ?? '—'}</td>
                  <td className="mono">{a.supplier_sku ?? a.sku}</td>
                  <td>{pack(a.pack_count, a.pack_size, a.uom)}</td>
                  <td className="num">{money(a.price)}{Number(a.price_change_request) > 0 && <div className="muted small">→ {money(a.price_change_request)} pending</div>}</td>
                  <td className="num">{unit(a.unit_price)}</td>
                  <td className="mono">{a.bids ?? '—'}</td>
                  <td><StatusPill status={a.offer_status} /></td>
                </tr>
              ))}
              {d.associations.length === 0 && <tr><td colSpan={7} className="loading">No supplier product is associated with this master.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <CategoriesCard ids={d.categories.map((c) => c.id)} shown={d.categories} canEdit={canEdit}
        note="Categories set here are kept when the catalog reloads from Magento, which reports any master whose categories it has changed since."
        onSave={async (ids) => {
          setMsg(''); setErr('');
          try { const r = await api.catalogSetMasterCategories(id, ids); setMsg(r.changed ? 'Categories saved.' : 'Nothing changed.'); load(); }
          catch (e) { setErr((e as Error).message); }
        }} />
      <div className="cards">
        <section className="card"><h2>Bids</h2>{d.bids.length ? d.bids.map((b) => <div key={b.id}>{b.name} <span className="mono">#{b.id}</span></div>) : <span className="muted">In no bid.</span>}</section>
        <section className="card"><h2>Attachments</h2>{d.attachments.length ? d.attachments.map((a) => <div key={a.id}>{a.title ?? a.filename} <span className="mono">{a.file_type ?? ''}</span></div>) : <span className="muted">None.</span>}</section>
      </div>
      <ChangeLog changes={d.changes} />
    </div>
  );
}

/** Add Quick Quote: a new master and its first supplier product in one step. */
function QuickQuotePane({ onDone }: { onDone: (masterId: number | null) => void }) {
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [f, setF] = useState({ master_name: '', master_sku: '', attribute_set_id: '24', supplier_sku: '', pack_count: '', pack_size: '', uom: '', price: '', catch_weight_per_unit: false });
  const [categoryId, setCategoryId] = useState<number | null>(QUICK_QUOTE_CATEGORY);
  const uoms = useLoad(() => api.catalogUom(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      const r = await api.catalogQuickQuote({ ...f, master_sku: f.master_sku || undefined, vendor_id: supplier?.id, category_id: categoryId ?? undefined });
      onDone(r.masterId);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={() => onDone(null)}>← Back to masters</button></div>
      <h1>Add Quick Quote</h1>
      <p className="sub">A new master and its first supplier product, associated, and the master filed in a category — the master's SKU is generated unless you give one</p>
      <Outcome msg="" err={err} />
      <section className="card">
        <div className="tf-grid">
          <label>Master name<input value={f.master_name} onChange={(e) => setF({ ...f, master_name: e.target.value })} /></label>
          <label>Master SKU (optional)<input value={f.master_sku} placeholder="generated" onChange={(e) => setF({ ...f, master_sku: e.target.value })} /></label>
          <label>Product type
            <select value={f.attribute_set_id} onChange={(e) => setF({ ...f, attribute_set_id: e.target.value })}>{Object.entries(SETS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
          <CategorySelect value={categoryId} onChange={setCategoryId} />
          <SupplierPicker value={supplier} onChange={setSupplier} />
          <label>Vendor item #<input value={f.supplier_sku} onChange={(e) => setF({ ...f, supplier_sku: e.target.value })} /></label>
          <label>Count<input inputMode="decimal" value={f.pack_count} onChange={(e) => setF({ ...f, pack_count: e.target.value })} /></label>
          <label>Pack size<input inputMode="decimal" value={f.pack_size} onChange={(e) => setF({ ...f, pack_size: e.target.value })} /></label>
          <label>UoM
            <select value={f.uom} onChange={(e) => setF({ ...f, uom: e.target.value })}><option value="">—</option>{uoms.data?.rows.map((u) => <option key={u.id}>{u.name}</option>)}</select>
          </label>
          <label>{f.catch_weight_per_unit ? 'Price per unit' : 'Case price'}<input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></label>
          <label className="chk"><input type="checkbox" checked={f.catch_weight_per_unit} onChange={(e) => setF({ ...f, catch_weight_per_unit: e.target.checked })} /> Catch weight, priced per unit</label>
        </div>
        <div className="edi-actions">
          <span className="muted">The unit price is computed from the pack with the same rule the camps' catalog ranks on.</span>
          <button className="btn-sm" disabled={busy || !supplier} onClick={save}>{busy ? 'Creating…' : 'Create master and supplier product'}</button>
        </div>
        <MagentoNote />
      </section>
    </div>
  );
}

// ── Supplier products ─────────────────────────────────────────────────────────────────────────────
const OFFER_VIEWS = [
  ['', 'All supplier products'], ['not_associated', 'Not associated'], ['without_orders', 'Without orders'], ['unavailable', 'Unavailable now'],
] as const;

export function OffersPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [f, setF] = useState({ view: '', status: '' });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [until, setUntil] = useState('');
  const sel = useSelection();
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const params = { q: dq, vendorId: supplier ? String(supplier.id) : '', ...f };
  const { data, err: loadErr } = useLoad(() => api.catalogOffers({ ...params, page: String(page) }), [JSON.stringify(params), page, nonce]);
  useEffect(() => { setPage(1); }, [JSON.stringify(params)]);

  async function bulk(set: Record<string, unknown>, what: string) {
    setMsg(''); setErr('');
    try {
      const r = await api.catalogBulkOffers([...sel.sel], set);
      setMsg(`${what}: ${r.changed.toLocaleString()} of ${r.selected.toLocaleString()} supplier products changed.`);
      sel.clear(); setNonce((n) => n + 1);
    } catch (e) { setErr((e as Error).message); }
  }

  if (open != null) return <OfferDetailPane id={open} canEdit={canEdit} onBack={() => { setOpen(null); setNonce((n) => n + 1); }} />;
  if (adding) return <AddSupplierItemPane onDone={(id) => { setAdding(false); if (id) setOpen(id); }} />;
  const ids = data?.rows.map((r) => r.id) ?? [];

  return (
    <div className="stack">
      <h1>Supplier products</h1>
      <p className="sub">Each supplier's own products, their packs and prices, and the masters they fill</p>
      <div className="toolbar">
        {OFFER_VIEWS.map(([v, l]) => <button key={v} className={`chip${f.view === v ? ' on' : ''}`} onClick={() => setF({ ...f, view: v })}>{l}</button>)}
      </div>
      <div className="toolbar toolbar-fields">
        <label className="field"><span>Search</span><input className="search" placeholder="Vendor item #, SKU or name…" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <SupplierPicker value={supplier} onChange={setSupplier} />
        <label className="field"><span>Status</span>
          <select className="sel" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="">Any status</option><option value="1">Enabled</option><option value="2">Disabled</option>
          </select>
        </label>
        <span className="toolbar-end">
          <a className="btn-sm ghost" href={`/api/commerce/catalog/offers-export.xlsx?${qsOf(params)}`} download
            title="The Supplier Products export — also the template for the availability import">Export xlsx</a>
          {canEdit && <button className="btn-sm" onClick={() => setAdding(true)}>Add Supplier Item</button>}
        </span>
      </div>
      <Outcome msg={msg} err={err || loadErr || ''} />
      {canEdit && sel.sel.size > 0 && (
        <div className="bulk-bar">
          <strong>{sel.sel.size.toLocaleString()} selected</strong>
          <button className="btn-sm" onClick={() => bulk({ status: 1 }, 'Enabled')}>Enable</button>
          <button className="btn-sm" onClick={() => bulk({ status: 2 }, 'Disabled')}>Disable</button>
          <span className="inline-field">Not available until
            <input type="date" className="sel" aria-label="Not available until" value={until} onChange={(e) => setUntil(e.target.value)} />
            <button className="btn-sm ghost" disabled={!until} onClick={() => bulk({ not_available_to: until }, `Unavailable until ${until}`)}>Set</button>
            <button className="btn-sm ghost" onClick={() => bulk({ not_available_to: null }, 'Availability cleared')}>Clear</button>
          </span>
          <button className="link" onClick={sel.clear}>Clear selection</button>
        </div>
      )}
      <section className="card">
        <div className="table-wrap">
          <table>
            <thead><tr>
              {canEdit && <th className="chk-col"><SelectAll ids={ids} selection={sel} /></th>}
              <th>Supplier</th><th>Vendor item #</th><th>Name</th><th>Master</th><th>Pack</th><th className="num">Price</th><th className="num">Unit price</th><th>Not avail. to</th><th>Status</th>
            </tr></thead>
            <tbody>
              {data?.rows.map((o) => (
                <tr key={o.id} className="row-click" onClick={() => setOpen(o.id)}>
                  {canEdit && <td className="chk-col" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Select ${o.supplier_sku ?? o.sku}`} checked={sel.has(o.id)} onChange={() => sel.toggle(o.id)} /></td>}
                  <td>{o.vendor ?? '—'}</td>
                  <td className="mono">{o.supplier_sku ?? '—'}</td>
                  <td style={{ fontWeight: 600 }}>{o.name ?? '—'}</td>
                  <td className="mono">{o.master_sku ?? '—'}</td>
                  <td>{pack(o.pack_count, o.pack_size, o.uom)}{o.is_catch_weight ? <span className="muted small"> · c/w</span> : null}</td>
                  <td className="num">{money(o.price)}</td>
                  <td className="num">{unit(o.unit_price)}</td>
                  <td className="mono">{o.not_available_to ?? '—'}</td>
                  <td><StatusPill status={o.status} /></td>
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={10} className="loading">No supplier products match.</td></tr>}
            </tbody>
          </table>
        </div>
        {!data && !loadErr && <p className="loading">Loading…</p>}
        {data && <Pager page={data.page} per={data.per} total={data.total} onPage={setPage} />}
      </section>
    </div>
  );
}

function OfferDetailPane({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const [d, setD] = useState<OfferDetail | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const uoms = useLoad(() => api.catalogUom(), []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const fields = (o: OfferDetail['offer']) => ({
    name: o.name ?? '', supplier_sku: o.supplier_sku ?? '', price: o.price == null ? '' : String(o.price), pack_count: o.pack_count == null ? '' : String(o.pack_count),
    pack_size: o.pack_size == null ? '' : String(o.pack_size), uom: o.uom ?? '', force_unit_price: Boolean(o.force_unit_price), is_catch_weight: Boolean(o.is_catch_weight),
    status: o.status, kosher: o.kosher, kosher_type: o.kosher_type, gluten_free: o.gluten_free, vegan: o.vegan, not_available_to: o.not_available_to ?? '',
  });
  const load = () => api.catalogOffer(id).then((r) => { setD(r); setForm(fields(r.offer)); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, [id]);

  if (!d) return <div className="stack"><div><button className="btn-sm ghost" onClick={onBack}>← Back to supplier products</button></div>{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  const o = d.offer;
  const original = fields(o) as Record<string, unknown>;
  const patch = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== original[k]));
  const set = (k: string, v: unknown) => setForm({ ...form, [k]: v });

  async function save() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.catalogSaveOffer(id, patch);
      setMsg(r.changed.length
        ? `Saved: ${r.changed.map((c) => c.replace(/_/g, ' ')).join(', ')}.${r.changed.includes('unit_price') ? ` Unit price is now ${unit(Number(r.unit_price))}.` : ''}`
        : 'Nothing changed.');
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to supplier products</button></div>
      <h1>{o.name ?? o.supplier_sku ?? o.sku}</h1>
      <p className="sub">{o.vendor ?? 'No supplier'} · item <span className="mono">{o.supplier_sku ?? '—'}</span> · SKU <span className="mono">{o.sku}</span>{o.master_sku ? <> · master <span className="mono">{o.master_sku}</span></> : null}</p>
      <p className="sub">Brand {o.brand ?? '—'} · {[o.special_order && 'Special order', o.direct_ship && 'Direct ship', o.early_cutoff && 'Early cutoff'].filter(Boolean).join(' · ') || 'Regular order type'} <span className="muted">(set by the bid spreadsheet)</span></p>
      <Outcome msg={msg} err={err} />
      <section className="card">
        <h2>Details</h2>
        <fieldset className="plain" disabled={!canEdit}>
          <div className="tf-grid">
            <label>Name<input value={String(form.name)} onChange={(e) => set('name', e.target.value)} /></label>
            <label>Vendor item #<input value={String(form.supplier_sku)} onChange={(e) => set('supplier_sku', e.target.value)} /></label>
            <label>Case price<input inputMode="decimal" value={String(form.price)} onChange={(e) => set('price', e.target.value)} /></label>
            <label>Count<input inputMode="decimal" value={String(form.pack_count)} onChange={(e) => set('pack_count', e.target.value)} /></label>
            <label>Pack size<input inputMode="decimal" value={String(form.pack_size)} onChange={(e) => set('pack_size', e.target.value)} /></label>
            <label>UoM
              <select value={String(form.uom)} onChange={(e) => set('uom', e.target.value || null)}>
                <option value="">—</option>{uoms.data?.rows.map((u) => <option key={u.id}>{u.name}</option>)}
              </select>
            </label>
            <label>Status<select value={String(form.status)} onChange={(e) => set('status', Number(e.target.value))}><option value="1">Enabled</option><option value="2">Disabled</option></select></label>
            <label>Not available until<input type="date" value={String(form.not_available_to)} onChange={(e) => set('not_available_to', e.target.value || null)} /></label>
            <label className="chk"><input type="checkbox" checked={Boolean(form.force_unit_price)} onChange={(e) => set('force_unit_price', e.target.checked)} /> Unit price per count (FUP)</label>
            <label className="chk"><input type="checkbox" checked={Boolean(form.is_catch_weight)} onChange={(e) => set('is_catch_weight', e.target.checked)} /> Catch weight</label>
            <label>Kosher<TriSelect label="Kosher" value={form.kosher as boolean | null} onChange={(v) => set('kosher', v)} /></label>
            <label>Kosher type
              <select value={String(form.kosher_type ?? '')} onChange={(e) => set('kosher_type', e.target.value || null)}><option value="">—</option>{KOSHER_TYPES.map((k) => <option key={k}>{k}</option>)}</select>
            </label>
            <label>Gluten-free<TriSelect label="Gluten-free" value={form.gluten_free as boolean | null} onChange={(v) => set('gluten_free', v)} /></label>
            <label>Vegan<TriSelect label="Vegan" value={form.vegan as boolean | null} onChange={(v) => set('vegan', v)} /></label>
          </div>
        </fieldset>
        <div className="edi-actions">
          <span className="muted">Unit price {unit(o.unit_price)} — recomputed from the price and pack with the rule the camps' catalog ranks on. A price change is logged in its price history.</span>
          {canEdit && <button className="btn-sm" disabled={busy || Object.keys(patch).length === 0} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>}
        </div>
        {canEdit && <MagentoNote />}
      </section>
      <section className="card">
        <h2>Masters it fills</h2>
        <table>
          <thead><tr><th>Master</th><th>Name</th><th>Bids</th><th>Assigned</th><th className="num">Pending price</th></tr></thead>
          <tbody>
            {d.associations.map((a) => (
              <tr key={a.id}><td className="mono">{a.master_sku ?? a.item_id}</td><td>{a.master_name ?? '—'}</td><td className="mono">{a.bids ?? '—'}</td>
                <td>{a.is_assign === 1 ? 'Yes' : 'No'}</td><td className="num">{Number(a.price_change_request) > 0 ? money(a.price_change_request) : '—'}</td></tr>
            ))}
            {d.associations.length === 0 && <tr><td colSpan={5} className="loading">Not associated with any master.</td></tr>}
          </tbody>
        </table>
      </section>
      <section className="card">
        <h2>Price history</h2>
        <table>
          <thead><tr><th>When (UTC)</th><th className="num">From</th><th className="num">To</th><th>By</th></tr></thead>
          <tbody>
            {d.history.map((h) => <tr key={h.id}><td className="mono">{ts(h.changed_at)}</td><td className="num">{money(h.old_price)}</td><td className="num">{money(h.new_price)}</td><td>{h.admin_user ?? 'feed'}</td></tr>)}
            {d.history.length === 0 && <tr><td colSpan={4} className="loading">No price changes on record.</td></tr>}
          </tbody>
        </table>
      </section>
      <ChangeLog changes={d.changes} />
    </div>
  );
}

/** Add Supplier Item: a supplier's product on one or more masters (created, or updated if it exists). */
function AddSupplierItemPane({ onDone }: { onDone: (offerId: number | null) => void }) {
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [f, setF] = useState({ master_sku: '', supplier_sku: '', name: '', pack_count: '', pack_size: '', uom: '', price: '', catch_weight_per_unit: false, force_unit_price: false });
  const uoms = useLoad(() => api.catalogUom(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      const r = await api.catalogAddOffer({ ...f, name: f.name || undefined, vendor_id: supplier?.id });
      onDone(r.offerId);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={() => onDone(null)}>← Back to supplier products</button></div>
      <h1>Add Supplier Item</h1>
      <p className="sub">A supplier's product on one or more masters. If the supplier already has this item number, it is updated and re-enabled.</p>
      <Outcome msg="" err={err} />
      <section className="card">
        <div className="tf-grid">
          <label>Master SKU(s), comma-separated<input value={f.master_sku} onChange={(e) => setF({ ...f, master_sku: e.target.value })} /></label>
          <SupplierPicker value={supplier} onChange={setSupplier} />
          <label>Vendor item #<input value={f.supplier_sku} onChange={(e) => setF({ ...f, supplier_sku: e.target.value })} /></label>
          <label>Name (optional)<input value={f.name} placeholder="the master's name" onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label>Count<input inputMode="decimal" value={f.pack_count} onChange={(e) => setF({ ...f, pack_count: e.target.value })} /></label>
          <label>Pack size<input inputMode="decimal" value={f.pack_size} onChange={(e) => setF({ ...f, pack_size: e.target.value })} /></label>
          <label>UoM<select value={f.uom} onChange={(e) => setF({ ...f, uom: e.target.value })}><option value="">—</option>{uoms.data?.rows.map((u) => <option key={u.id}>{u.name}</option>)}</select></label>
          <label>{f.catch_weight_per_unit ? 'Price per unit' : 'Case price'}<input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></label>
          <label className="chk"><input type="checkbox" checked={f.catch_weight_per_unit} onChange={(e) => setF({ ...f, catch_weight_per_unit: e.target.checked })} /> Catch weight, priced per unit</label>
          <label className="chk"><input type="checkbox" checked={f.force_unit_price} onChange={(e) => setF({ ...f, force_unit_price: e.target.checked })} /> Unit price per count (FUP)</label>
        </div>
        <div className="edi-actions">
          <span className="muted">Each master must be enabled. The product is associated with every one, and joins the bids those masters are in.</span>
          <button className="btn-sm" disabled={busy || !supplier} onClick={save}>{busy ? 'Saving…' : 'Save supplier item'}</button>
        </div>
        <MagentoNote />
      </section>
    </div>
  );
}

// ── Units of measure & brands ─────────────────────────────────────────────────────────────────────
export function CatalogSetupPanel({ canEdit }: { canEdit: boolean }) {
  return (
    <div className="stack">
      <h1>Units &amp; brands</h1>
      <p className="sub">The units a pack is measured in — each one's factor is what the unit price divides by — and the brand list masters pick from</p>
      <UomCard canEdit={canEdit} />
      <BrandsCard canEdit={canEdit} />
    </div>
  );
}

function UomCard({ canEdit }: { canEdit: boolean }) {
  const [nonce, setNonce] = useState(0);
  const { data, err } = useLoad(() => api.catalogUom(), [nonce]);
  const [edit, setEdit] = useState<Record<number, { factor: string; classification: string }>>({});
  const [add, setAdd] = useState({ name: '', factor: '', classification: 'Quantity' });
  const [msg, setMsg] = useState('');
  const [bad, setBad] = useState('');
  const run = async (fn: () => Promise<string>) => {
    setMsg(''); setBad('');
    try { setMsg(await fn()); setNonce((n) => n + 1); } catch (e) { setBad((e as Error).message); }
  };
  const row = (u: UomRow) => edit[u.id] ?? { factor: String(u.factor), classification: u.classification };
  return (
    <section className="card">
      <h2>Units of measure</h2>
      <Outcome msg={msg} err={bad || err || ''} />
      <div className="table-wrap">
        <table>
          <thead><tr><th>Unit</th><th>Factor</th><th>Classification</th><th className="num">Supplier products</th><th></th></tr></thead>
          <tbody>
            {data?.rows.map((u) => {
              const r = row(u);
              const dirty = Number(r.factor) !== Number(u.factor) || r.classification !== u.classification;
              return (
                <tr key={u.id}>
                  <td className="mono">{u.name}</td>
                  <td><input className="search short" aria-label={`Factor for ${u.name}`} disabled={!canEdit} value={r.factor} onChange={(e) => setEdit({ ...edit, [u.id]: { ...r, factor: e.target.value } })} /></td>
                  <td>
                    <select className="sel" aria-label={`Classification for ${u.name}`} disabled={!canEdit} value={r.classification} onChange={(e) => setEdit({ ...edit, [u.id]: { ...r, classification: e.target.value } })}>
                      {data.classifications.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="num">{u.products.toLocaleString()}</td>
                  <td className="actions">
                    {canEdit && <button className="btn-sm" disabled={!dirty} onClick={() => run(async () => {
                      const res = await api.catalogSaveUom(u.id, { factor: r.factor, classification: r.classification });
                      setEdit((x) => { const n = { ...x }; delete n[u.id]; return n; });
                      return `${u.name} saved${res.repriced ? ` — ${res.repriced.toLocaleString()} supplier products re-priced per unit` : ''}.`;
                    })}>Save</button>}
                    {canEdit && u.products === 0 && <button className="link" onClick={() => run(async () => { await api.catalogDeleteUom(u.id); return `${u.name} removed.`; })}>Remove</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!data && !err && <p className="loading">Loading…</p>}
      {canEdit && data && (
        <div className="form-row" style={{ marginTop: 12 }}>
          <label className="field"><span>New unit</span><input className="search short" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} /></label>
          <label className="field"><span>Factor</span><input className="search short" inputMode="decimal" value={add.factor} onChange={(e) => setAdd({ ...add, factor: e.target.value })} /></label>
          <label className="field"><span>Classification</span>
            <select className="sel" value={add.classification} onChange={(e) => setAdd({ ...add, classification: e.target.value })}>{data.classifications.map((c) => <option key={c}>{c}</option>)}</select>
          </label>
          <button className="btn-sm" disabled={!add.name || !add.factor} onClick={() => run(async () => {
            await api.catalogAddUom({ name: add.name, factor: Number(add.factor), classification: add.classification });
            setAdd({ name: '', factor: '', classification: add.classification });
            return `${add.name} added.`;
          })}>Add unit</button>
        </div>
      )}
      <p className="muted small">A unit in use cannot be renamed or removed. Changing a factor re-prices every supplier product in that unit, as Magento's nightly recalculation would.</p>
    </section>
  );
}

function BrandsCard({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const [nonce, setNonce] = useState(0);
  const { data, err } = useLoad(() => api.catalogBrands(dq), [dq, nonce]);
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [bad, setBad] = useState('');
  const run = async (fn: () => Promise<string>) => {
    setMsg(''); setBad('');
    try { setMsg(await fn()); setNonce((n) => n + 1); } catch (e) { setBad((e as Error).message); }
  };
  return (
    <section className="card">
      <h2>Brands {data && <span className="muted">({data.total.toLocaleString()})</span>}</h2>
      <Outcome msg={msg} err={bad || err || ''} />
      <div className="toolbar">
        <input className="search" placeholder="Search brands…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canEdit && (
          <span className="inline-field">
            <input className="search" placeholder="New brand" value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn-sm" disabled={!name.trim()} onClick={() => run(async () => { const r = await api.catalogAddBrand(name); setName(''); return r.message; })}>Add brand</button>
          </span>
        )}
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Brand</th><th className="num">Masters</th><th></th></tr></thead>
          <tbody>
            {data?.rows.map((b) => (
              <tr key={b.id}>
                <td>{renaming?.id === b.id
                  ? <input className="search" aria-label={`Rename ${b.name}`} value={renaming.name} onChange={(e) => setRenaming({ id: b.id, name: e.target.value })} />
                  : b.name}</td>
                <td className="num">{b.masters.toLocaleString()}</td>
                <td className="actions">
                  {canEdit && renaming?.id === b.id && <>
                    <button className="btn-sm" onClick={() => run(async () => { await api.catalogRenameBrand(b.id, renaming.name); setRenaming(null); return 'Brand renamed.'; })}>Save</button>
                    <button className="link" onClick={() => setRenaming(null)}>Cancel</button>
                  </>}
                  {canEdit && renaming?.id !== b.id && <button className="link edit" onClick={() => setRenaming({ id: b.id, name: b.name })}>Rename</button>}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && <tr><td colSpan={3} className="loading">No brands match.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="muted small">Brands are stored upper-case, as Magento stores them. Showing the first 100 matches.</p>
    </section>
  );
}

// ── Imports ───────────────────────────────────────────────────────────────────────────────────────
export function CatalogImportsPanel({ canEdit }: { canEdit: boolean }) {
  const [nonce, setNonce] = useState(0);
  const [kind, setKind] = useState('');
  const { data, err } = useLoad(() => api.catalogImports(kind), [kind, nonce]);
  const [open, setOpen] = useState<number | null>(null);
  const detail = useLoad(() => (open ? api.catalogImport(open) : Promise.resolve(null)), [open]);
  return (
    <div className="stack">
      <h1>Imports</h1>
      <p className="sub">Import Supplier Products — the availability file — and the record of every catalog and bid import and export</p>
      {canEdit && <AvailabilityImportCard onDone={() => setNonce((n) => n + 1)} />}
      <section className="card">
        <h2>History</h2>
        <div className="toolbar">
          {[['', 'All'], ['availability', 'Availability'], ['bid', 'Bid imports'], ['bid_export', 'Bid exports']].map(([k, l]) => (
            <button key={k} className={`chip${kind === k ? ' on' : ''}`} onClick={() => setKind(k)}>{l}</button>
          ))}
        </div>
        <Outcome msg="" err={err ?? ''} />
        <div className="table-wrap">
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Supplier</th><th>File</th><th>Result</th></tr></thead>
            <tbody>
              {data?.map((r) => (
                <tr key={r.id} className="row-click" onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="mono">{ts(r.created_at)}</td>
                  <td>{r.created_by}</td>
                  <td>{r.kind === 'bid_export' ? 'Bid export' : r.kind === 'bid' ? `Bid import${r.mode ? ` (${r.mode.replace(/_/g, ' ')})` : ''}` : 'Availability'}{r.bid ? <div className="muted small">{r.bid}</div> : null}{r.dry_run ? <span className="pill info"> preview</span> : null}</td>
                  <td>{r.vendor ?? r.vendor_id}</td>
                  <td className="mono">{r.filename ?? '—'}</td>
                  <td>{r.status}{r.message_count > 0 ? <span className="muted small"> · {r.message_count} messages</span> : null}</td>
                </tr>
              ))}
              {data && data.length === 0 && <tr><td colSpan={6} className="loading">No imports yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {open && detail.data && detail.data.messages.length > 0 && (
          <ul className="import-messages">{detail.data.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
        )}
      </section>
    </div>
  );
}

function AvailabilityImportCard({ onDone }: { onDone: () => void }) {
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState('');
  const [r, setR] = useState<ImportResult | null>(null);
  const [err, setErr] = useState('');
  async function go(dryRun: boolean) {
    if (!supplier || !file) return;
    setBusy(dryRun ? 'preview' : 'import'); setErr(''); setR(null);
    try { setR(await api.catalogAvailabilityImport(supplier.id, file, dryRun)); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(''); }
  }
  return (
    <section className="card">
      <h2>Import Supplier Products (availability)</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Export the supplier's products from Supplier Products, fill in the last column — a date sets the item's “not available until”,
        0 clears it, “disable” disables it — and import the file here. Preview first: it runs every change and rolls it back.
      </p>
      <div className="form-row">
        <SupplierPicker value={supplier} onChange={setSupplier} />
        <label className="field"><span>File (.xlsx)</span><input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
        <button className="btn-sm ghost" disabled={!supplier || !file || busy !== ''} onClick={() => go(true)}>{busy === 'preview' ? 'Previewing…' : 'Preview'}</button>
        <button className="btn-sm" disabled={!supplier || !file || busy !== ''} onClick={() => go(false)}>{busy === 'import' ? 'Importing…' : 'Import'}</button>
      </div>
      <Outcome msg="" err={err} />
      {r && <ImportOutcome r={r} />}
      <MagentoNote />
    </section>
  );
}
